/**
 * Authenticated Copilot Studio execution through Microsoft's Direct-to-Engine
 * API and the official Microsoft Agents SDK.
 *
 * Direct Line cannot run agents configured for Integrated (Microsoft Entra)
 * authentication. Direct-to-Engine can: the signed-in user's delegated Power
 * Platform token is sent to the published agent's `/conversations` endpoint.
 * The app registration must have `CopilotStudio.Copilots.Invoke`; this module
 * never provisions or requests the legacy broad Power Virtual Agents scopes.
 *
 * A fresh SDK client is created for every call. This avoids the SDK's mutable
 * current-conversation state crossing concurrent MCP requests. Continuations
 * always pass the caller's conversation ID explicitly.
 */

import { Activity } from "@microsoft/agents-activity";
import {
  ConnectionSettings,
  CopilotStudioClient,
  getCopilotStudioConnectionUrl,
  type StartRequest,
} from "@microsoft/agents-copilotstudio-client";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  isPowerAutomateError,
  ValidationError,
  wrapApiCall,
} from "../utils/errors.js";
import { createChildLogger } from "../utils/logger.js";
import { GUID_PATTERN, isPrivateHost, sanitizeErrorMessage } from "../utils/security.js";

const logger = createChildLogger({ module: "copilot-studio-execution-api" });

export const COPILOT_EXECUTION_API_VERSION = "2022-03-01-preview";
export const COPILOT_EXECUTION_DELEGATED_PERMISSION = "CopilotStudio.Copilots.Invoke";
export const COPILOT_EXECUTION_MAX_MESSAGE_CHARS = 32_000;
export const COPILOT_EXECUTION_MAX_ATTACHMENTS = 5;
export const COPILOT_EXECUTION_MAX_REQUEST_BYTES = 256 * 1024;
export const COPILOT_EXECUTION_DEFAULT_RESPONSE_CHARS = 20_000;
export const COPILOT_EXECUTION_MAX_RESPONSE_CHARS = 100_000;
export const COPILOT_EXECUTION_DEFAULT_TIMEOUT_MS = 120_000;
export const COPILOT_EXECUTION_MAX_TIMEOUT_MS = 180_000;

const MAX_RESPONSE_ACTIVITIES = 100;
const MAX_RESPONSE_MESSAGES = 50;
const MAX_RESPONSE_ATTACHMENTS = 20;
const MAX_RESPONSE_ACTIVITY_SIGNALS = 20;
// Official Activity end-of-conversation codes only. ContentFiltered is a
// documented Copilot error, but not a documented value for this protocol field.
// A message containing these words is never a protocol status signal.
const RECOGNIZED_END_CODES = [
  "unknown",
  "completedSuccessfully",
  "userCancelled",
  "agentTimedOut",
  "agentIssuedInvalidMessage",
  "channelFailed",
] as const;
type CopilotConversationEndCode = (typeof RECOGNIZED_END_CODES)[number];
const MAX_SAFE_ERROR_CHARS = 4_096;
const MAX_CARD_NODES = 200;
const MAX_CARD_DEPTH = 8;
const MAX_CARD_TEXT_BYTES = 8_192;
const MAX_CARD_FIELD_BYTES = 1_024;
const CARD_SECRET_LABEL =
  "(?:authorization[_ -]?code|verification[_ -]?code|code|state|nonce|assertion|session(?:[_ -]?id)?|client[_ -]?secret|api[_ -]?key|access[_ -]?token|refresh[_ -]?token|id[_ -]?token|password|signature|sig|key|secret|token)";
const CARD_SECRET_TEXT_ASSIGNMENT = new RegExp(
  `(["']?\\b${CARD_SECRET_LABEL}\\b["']?\\s*(?:[:=]|is\\b)\\s*)(?:"(?:\\\\.|[^"\\\\])*(?:"|$)|'(?:\\\\.|[^'\\\\])*(?:'|$)|["'][\\s\\S]*$|[^\\s,;<>"']+)`,
  "gi",
);
const CARD_SECRET_PATH_LABEL = new RegExp(`^${CARD_SECRET_LABEL}$`, "i");
const CARD_SECRET_PATH_ASSIGNMENT = new RegExp(`\\b${CARD_SECRET_LABEL}\\s*[:=]`, "i");
const CARD_ACTION_TYPES = new Set([
  "Action.OpenUrl",
  "Action.Submit",
  "Action.Execute",
  "Action.ShowCard",
  "Action.ToggleVisibility",
]);

export const COPILOT_AGENT_SCHEMA_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,199}$/;
export const COPILOT_CONVERSATION_ID_PATTERN = /^[\x21-\x7e]{1,512}$/;
export const COPILOT_LOCALE_PATTERN = /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/;
export const COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN = /^[^\s/]+\/[^\s/]+$/;

export interface CopilotExecutionSdkClient {
  startConversationWithResponse(request?: StartRequest | boolean): Promise<unknown>;
  executeStreaming(activity: Activity, conversationId: string): AsyncIterable<unknown>;
}

export type CopilotExecutionSdkClientFactory = (
  settings: ConnectionSettings,
  token: string,
) => CopilotExecutionSdkClient;

export interface CopilotStudioExecutionApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
  /** Test seam; production uses Microsoft's official Copilot Studio client. */
  clientFactory?: CopilotExecutionSdkClientFactory;
}

export interface CopilotExecutionAttachment {
  contentType: string;
  contentUrl?: string;
  content?: Record<string, unknown>;
  name?: string;
  thumbnailUrl?: string;
}

export interface CopilotExecutionResponseAttachment {
  contentType: string;
  name?: string;
  hasInlineContent: boolean;
  hasContentUrl: boolean;
  cardDiagnostics?: CopilotResponseCardDiagnostics;
}

export interface CopilotResponseCardDiagnostics {
  stage: "start" | "turn";
  availability: "returned" | "unavailable";
  textBlocks: string[];
  links: Array<{ label: string | null; url: { origin: string; pathname: string } }>;
  actions: Array<{
    type: string;
    title?: string;
    url?: { origin: string; pathname: string };
    urlOmitted?: boolean;
  }>;
  truncated: boolean;
  unsupportedElementsOmitted: boolean;
  omitted: { actionData: true; inputs: true; rawContent: true; authentication: true };
  limitations: string;
}

export interface ExecuteCopilotAgentOptions {
  /** Published, case-sensitive Dataverse schema name (for example cr123_myAgent). */
  agentSchemaName: string;
  message: string;
  environmentId?: string;
  conversationId?: string;
  locale?: string;
  attachments?: CopilotExecutionAttachment[];
  /** Maximum returned agent text after redaction. */
  maxResponseChars?: number;
  timeoutMs?: number;
  /** Optional redacted, bounded card metadata; never submits or opens a card action. */
  includeResponseCardDiagnostics?: boolean;
}

export interface CopilotExecutionResult {
  conversationId: string;
  responses: string[];
  /** Legacy field: only the SDK iterator returned, not proof of service/action success. */
  completed: boolean;
  completionMeaning: "sdk_iterator_returned";
  actionOutcome: "unverified";
  activitySignals: CopilotExecutionActivitySignals;
  responseTruncated: boolean;
  responseAttachments: CopilotExecutionResponseAttachment[];
}

export interface CopilotExecutionActivitySignals {
  availability: "reported" | "unavailable";
  signals: Array<{
    stage: "start" | "turn";
    provenance: "sdk_activity";
    type: "endOfConversation";
    codeStatus: "recognized" | "missing" | "unrecognized" | "invalid";
    code?: CopilotConversationEndCode;
  }>;
  truncated: boolean;
  limitations: string;
}

interface NormalizedOptions {
  agentSchemaName: string;
  message: string;
  environmentId: string;
  conversationId?: string;
  locale?: string;
  attachments?: CopilotExecutionAttachment[];
  maxResponseChars: number;
  timeoutMs: number;
  includeResponseCardDiagnostics: boolean;
}

interface CollectedActivities {
  activities: Record<string, unknown>[];
  responseTruncated: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function stripUnsafeControlCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint === 0x09 || codePoint === 0x0a || codePoint === 0x0d || codePoint >= 0x20;
    })
    .filter((character) => character.codePointAt(0) !== 0x7f)
    .join("");
}

function validateEnvironmentId(environmentId: string): string {
  const normalized = environmentId.trim();
  const guid = normalized.replace(/^Default-/i, "");
  if (!GUID_PATTERN.test(guid)) {
    throw new ValidationError(
      "environmentId must be a GUID or Default-{GUID} to address Copilot Studio",
    );
  }
  return normalized;
}

/**
 * Derive the production environment API origin used by Direct-to-Engine.
 *
 * `Default-` is part of a default environment's DNS label. Splitting the
 * complete hyphen-free ID before its final two characters matches the
 * official Agents SDK for both GUID and Default-{GUID} environments.
 */
export function copilotStudioEnvironmentApiOrigin(environmentId: string): string {
  const normalized = validateEnvironmentId(environmentId);
  const compact = normalized.replace(/-/g, "").toLowerCase();
  return `https://${compact.slice(0, -2)}.${compact.slice(-2)}.environment.api.powerplatform.com`;
}

function createConnectionSettings(
  environmentId: string,
  agentSchemaName: string,
): ConnectionSettings {
  return new ConnectionSettings({
    environmentId: validateEnvironmentId(environmentId),
    schemaName: agentSchemaName,
    enableDiagnostics: false,
  });
}

/** Exact URL the official SDK uses; exported for diagnostics and regression tests. */
export function copilotStudioExecutionUrl(
  environmentId: string,
  agentSchemaName: string,
  conversationId?: string,
): string {
  if (!COPILOT_AGENT_SCHEMA_NAME_PATTERN.test(agentSchemaName)) {
    throw new ValidationError("agentSchemaName must be the case-sensitive published schema name");
  }
  if (conversationId !== undefined && !COPILOT_CONVERSATION_ID_PATTERN.test(conversationId)) {
    throw new ValidationError("conversationId must be printable ASCII without whitespace");
  }
  return getCopilotStudioConnectionUrl(
    createConnectionSettings(environmentId, agentSchemaName),
    conversationId,
  );
}

function validateHttpsAttachmentUrl(value: string, field: string): string {
  if (value.length > 2_048) {
    throw new ValidationError(`${field} must contain at most 2048 characters`);
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new ValidationError(`${field} must be a valid HTTPS URL`);
  }
  if (parsed.protocol !== "https:" || !parsed.hostname || isPrivateHost(parsed.hostname)) {
    throw new ValidationError(`${field} must be a public HTTPS URL`);
  }
  return value;
}

function validateAttachment(
  attachment: CopilotExecutionAttachment,
  index: number,
): CopilotExecutionAttachment {
  if (
    !COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN.test(attachment.contentType) ||
    attachment.contentType.length > 200
  ) {
    throw new ValidationError(
      `attachments[${index}].contentType must be a MIME type of at most 200 characters`,
    );
  }
  if (attachment.name !== undefined) {
    if (
      attachment.name.length < 1 ||
      attachment.name.length > 255 ||
      hasControlCharacter(attachment.name)
    ) {
      throw new ValidationError(
        `attachments[${index}].name must contain 1 to 255 characters and no control characters`,
      );
    }
  }
  if (attachment.content !== undefined && !isPlainObject(attachment.content)) {
    throw new ValidationError(`attachments[${index}].content must be a JSON object`);
  }
  if (attachment.content === undefined && attachment.contentUrl === undefined) {
    throw new ValidationError(`attachments[${index}] must include content or contentUrl`);
  }

  return {
    contentType: attachment.contentType,
    ...(attachment.contentUrl !== undefined
      ? {
          contentUrl: validateHttpsAttachmentUrl(
            attachment.contentUrl,
            `attachments[${index}].contentUrl`,
          ),
        }
      : {}),
    ...(attachment.content !== undefined ? { content: attachment.content } : {}),
    ...(attachment.name !== undefined ? { name: attachment.name } : {}),
    ...(attachment.thumbnailUrl !== undefined
      ? {
          thumbnailUrl: validateHttpsAttachmentUrl(
            attachment.thumbnailUrl,
            `attachments[${index}].thumbnailUrl`,
          ),
        }
      : {}),
  };
}

function integerInRange(value: number, min: number, max: number, field: string): number {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new ValidationError(`${field} must be an integer from ${min} to ${max}`);
  }
  return value;
}

function normalizeOptions(
  options: ExecuteCopilotAgentOptions,
  defaultEnvironmentId: string,
): NormalizedOptions {
  if (
    options.includeResponseCardDiagnostics !== undefined &&
    typeof options.includeResponseCardDiagnostics !== "boolean"
  ) {
    throw new ValidationError("includeResponseCardDiagnostics must be a boolean");
  }
  if (!COPILOT_AGENT_SCHEMA_NAME_PATTERN.test(options.agentSchemaName)) {
    throw new ValidationError(
      "agentSchemaName must be the case-sensitive published schema name (letters, numbers, and underscores; starts with a letter)",
    );
  }
  if (
    options.message.trim().length === 0 ||
    options.message.length > COPILOT_EXECUTION_MAX_MESSAGE_CHARS
  ) {
    throw new ValidationError(
      `message must contain 1 to ${COPILOT_EXECUTION_MAX_MESSAGE_CHARS} characters`,
    );
  }
  if (
    options.conversationId !== undefined &&
    !COPILOT_CONVERSATION_ID_PATTERN.test(options.conversationId)
  ) {
    throw new ValidationError(
      "conversationId must contain 1 to 512 printable ASCII characters and no whitespace",
    );
  }
  if (options.locale !== undefined && !COPILOT_LOCALE_PATTERN.test(options.locale)) {
    throw new ValidationError("locale must be a BCP-47 language tag such as en-US");
  }
  if ((options.attachments?.length ?? 0) > COPILOT_EXECUTION_MAX_ATTACHMENTS) {
    throw new ValidationError(
      `attachments must contain at most ${COPILOT_EXECUTION_MAX_ATTACHMENTS} items`,
    );
  }

  const environmentId = validateEnvironmentId(options.environmentId ?? defaultEnvironmentId);
  const attachments = options.attachments?.map(validateAttachment);
  const maxResponseChars = integerInRange(
    options.maxResponseChars ?? COPILOT_EXECUTION_DEFAULT_RESPONSE_CHARS,
    1,
    COPILOT_EXECUTION_MAX_RESPONSE_CHARS,
    "maxResponseChars",
  );
  const timeoutMs = integerInRange(
    options.timeoutMs ?? COPILOT_EXECUTION_DEFAULT_TIMEOUT_MS,
    1_000,
    COPILOT_EXECUTION_MAX_TIMEOUT_MS,
    "timeoutMs",
  );

  const requestShape = {
    type: "message",
    text: options.message,
    ...(options.locale !== undefined ? { locale: options.locale } : {}),
    ...(attachments !== undefined ? { attachments } : {}),
  };
  let serialized: string;
  try {
    serialized = JSON.stringify(requestShape);
  } catch {
    throw new ValidationError("Copilot execution request must contain JSON-serializable values");
  }
  if (Buffer.byteLength(serialized, "utf8") > COPILOT_EXECUTION_MAX_REQUEST_BYTES) {
    throw new ValidationError(
      `Copilot execution request exceeds ${COPILOT_EXECUTION_MAX_REQUEST_BYTES} bytes`,
    );
  }

  return {
    agentSchemaName: options.agentSchemaName,
    message: options.message,
    environmentId,
    ...(options.conversationId !== undefined ? { conversationId: options.conversationId } : {}),
    ...(options.locale !== undefined ? { locale: options.locale } : {}),
    ...(attachments !== undefined ? { attachments } : {}),
    maxResponseChars,
    timeoutMs,
    includeResponseCardDiagnostics: options.includeResponseCardDiagnostics ?? false,
  };
}

function redactSecrets(value: string): string {
  return value
    .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, "Bearer [REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[JWT REDACTED]")
    .replace(
      /(["']?(?:access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|api[_-]?key|password)["']?\s*[:=]\s*["']?)[^"'\s,}&]+/gi,
      "$1[REDACTED]",
    )
    .replace(
      /([?&](?:access[_-]?token|refresh[_-]?token|token|secret|sig|api[_-]?key)=)[^&\s]+/gi,
      "$1[REDACTED]",
    );
}

function safeAgentText(value: string, maxChars: number): { value: string; truncated: boolean } {
  // Bound work before applying regexes. The extra room lets a leading secret be
  // replaced before the final output limit is applied.
  const workLimit = Math.min(value.length, maxChars + 8_192);
  const safe = stripUnsafeControlCharacters(redactSecrets(value.slice(0, workLimit)));
  return {
    value: safe.slice(0, maxChars),
    truncated: value.length > workLimit || safe.length > maxChars,
  };
}

function firstOwnValue(object: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(object, key)) return object[key];
  }
  return undefined;
}

function validatedStartConversationId(raw: unknown): string | undefined {
  if (!isPlainObject(raw)) return undefined;
  const candidates = ["conversationId", "ConversationId", "conversationID"]
    .map((key) => raw[key])
    .filter((value): value is string => typeof value === "string");
  const unique = [...new Set(candidates)];
  return unique.length === 1 && COPILOT_CONVERSATION_ID_PATTERN.test(unique[0]!)
    ? unique[0]
    : undefined;
}

function readStartResponse(raw: unknown): {
  conversationId: string;
  activities: unknown[];
} {
  if (!isPlainObject(raw)) {
    throw new ApiError("Start Copilot Studio conversation: SDK returned an invalid response", 502);
  }
  const candidates = ["conversationId", "ConversationId", "conversationID"]
    .map((key) => raw[key])
    .filter((value): value is string => typeof value === "string");
  const unique = [...new Set(candidates)];
  if (unique.length !== 1 || !COPILOT_CONVERSATION_ID_PATTERN.test(unique[0]!)) {
    throw new ApiError(
      unique.length > 1
        ? "Start Copilot Studio conversation: SDK returned conflicting conversation ids"
        : "Start Copilot Studio conversation: SDK did not return a valid conversation id",
      502,
    );
  }
  const activities = firstOwnValue(raw, ["activities", "Activities"]);
  if (!Array.isArray(activities)) {
    throw new ApiError("Start Copilot Studio conversation: SDK returned invalid activities", 502);
  }
  if (activities.length > MAX_RESPONSE_ACTIVITIES) {
    throw new ApiError(
      `Start Copilot Studio conversation: response exceeded ${MAX_RESPONSE_ACTIVITIES} activities`,
      502,
    );
  }
  return { conversationId: unique[0]!, activities };
}

function safeSdkErrorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error);
  return sanitizeErrorMessage(redactSecrets(raw)).slice(0, MAX_SAFE_ERROR_CHARS);
}

function throwSdkError(error: unknown, operation: string): never {
  if (isPowerAutomateError(error)) throw error;
  const upstream = safeSdkErrorMessage(error);
  const statusMatch = upstream.match(/(?:^|\D)([45]\d{2})(?:\D|$)/);
  const status = statusMatch ? Number(statusMatch[1]) : undefined;
  if (status === 401) {
    throw new AuthenticationError(`${operation}: upstream authentication failed. ${upstream}`);
  }
  if (status === 403) {
    throw new AuthorizationError(
      `${operation}: upstream authorization denied. Required delegated Power Platform permission: ${COPILOT_EXECUTION_DELEGATED_PERMISSION}. Upstream: ${upstream}`,
    );
  }
  if (status === 429) {
    throw new ApiError(`${operation}: upstream rate limited. ${upstream}`, 429);
  }
  throw new ApiError(
    `${operation}: ${upstream || "the Microsoft Copilot Studio SDK failed"}`,
    status ?? 502,
  );
}

async function beforeDeadline<T>(
  operation: Promise<T>,
  deadline: number,
  label: string,
): Promise<T> {
  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    throw new ApiError(`${label} timed out before completion`, 504);
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new ApiError(`${label} timed out before completion`, 504)),
      remaining,
    );
    timer.unref?.();
  });
  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function activityObject(raw: unknown): Record<string, unknown> {
  if (!isPlainObject(raw)) {
    throw new ApiError("Execute Copilot Studio agent: SDK returned a malformed activity", 502);
  }
  if (typeof raw["type"] !== "string" || raw["type"].length > 128) {
    throw new ApiError("Execute Copilot Studio agent: SDK returned an invalid activity type", 502);
  }
  if (raw["text"] !== undefined && typeof raw["text"] !== "string") {
    throw new ApiError("Execute Copilot Studio agent: SDK returned non-text message content", 502);
  }
  if (raw["attachments"] !== undefined && !Array.isArray(raw["attachments"])) {
    throw new ApiError("Execute Copilot Studio agent: SDK returned invalid attachments", 502);
  }
  return raw;
}

async function collectStream(
  stream: AsyncIterable<unknown>,
  existingActivities: Record<string, unknown>[],
  deadline: number,
): Promise<CollectedActivities> {
  const iterator = stream[Symbol.asyncIterator]();
  let completed = false;
  try {
    while (existingActivities.length <= MAX_RESPONSE_ACTIVITIES) {
      const next = await beforeDeadline(iterator.next(), deadline, "Execute Copilot Studio agent");
      if (next.done) {
        completed = true;
        break;
      }
      existingActivities.push(activityObject(next.value));
    }
    if (!completed) {
      throw new ApiError(
        `Execute Copilot Studio agent: response exceeded ${MAX_RESPONSE_ACTIVITIES} activities`,
        502,
      );
    }
    return { activities: existingActivities, responseTruncated: false };
  } finally {
    if (!completed && iterator.return !== undefined) {
      const close = iterator.return();
      void close.catch((error: unknown) => {
        logger.warn(
          { error: safeSdkErrorMessage(error) },
          "Copilot Studio stream cleanup failed after the primary request ended",
        );
      });
    }
  }
}

function validateActivityConversationIds(
  activities: Record<string, unknown>[],
  expectedConversationId: string,
): void {
  for (const activity of activities) {
    const conversation = activity["conversation"];
    if (conversation === undefined) continue;
    if (!isPlainObject(conversation)) {
      throw new ApiError("Execute Copilot Studio agent: invalid activity conversation", 502);
    }
    const id = conversation["id"];
    if (id !== undefined && id !== expectedConversationId) {
      throw new ApiError(
        "Execute Copilot Studio agent: activity returned a conflicting conversation id",
        502,
      );
    }
  }
}

/** Metadata only, based on Microsoft's AdaptiveCards 1.6 schema. Never invokes actions. */
function responseCardDiagnostics(
  content: unknown,
  stage: "start" | "turn",
  budget: { nodes: number; textBytes: number; links: number },
): CopilotResponseCardDiagnostics {
  const result: CopilotResponseCardDiagnostics = {
    stage,
    availability: "returned",
    textBlocks: [],
    links: [],
    actions: [],
    truncated: false,
    unsupportedElementsOmitted: false,
    omitted: { actionData: true, inputs: true, rawContent: true, authentication: true },
    limitations:
      "Redacted diagnostic metadata, not a rendered or submitted card. URLs are sanitized HTTPS origin/path only; query, fragment and userinfo are omitted and no URL is opened. Unsupported elements, action data, inputs and authentication payloads are not inspected. Card presence does not establish consent requirements. Completed means the response stream ended, not that the requested ticket action succeeded.",
  };
  const own = (object: Record<string, unknown>, key: string): unknown => {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);
    if (!descriptor) return undefined;
    if (!Object.hasOwn(descriptor, "value")) throw new Error("accessor");
    return descriptor.value;
  };
  const object = (value: unknown): Record<string, unknown> | undefined => {
    if (!isPlainObject(value)) return undefined;
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null ? value : undefined;
  };
  const takeText = (raw: unknown): string | undefined => {
    if (typeof raw !== "string") return undefined;
    const bounded = raw.slice(0, 4_096);
    const safe = sanitizeErrorMessage(stripUnsafeControlCharacters(redactSecrets(bounded)))
      .replace(CARD_SECRET_TEXT_ASSIGNMENT, "$1[REDACTED]")
      .replace(/\b[a-z][a-z0-9+.-]*:(?:\/\/)?[^\s<>"']+/gi, "[URL REDACTED]")
      .replace(/(?:\bwww\.|\/\/)[^\s<>"']+/gi, "[URL REDACTED]")
      .replace(/[A-Za-z0-9_~+/-]{32,}={0,2}/g, "[OPAQUE VALUE REDACTED]");
    const limit = Math.min(MAX_CARD_FIELD_BYTES, budget.textBytes);
    let value = "";
    let bytes = 0;
    for (const character of safe) {
      const size = Buffer.byteLength(character, "utf8");
      if (bytes + size > limit) break;
      value += character;
      bytes += size;
    }
    budget.textBytes -= bytes;
    result.truncated ||= raw.length > bounded.length || value.length < safe.length;
    return value || undefined;
  };
  const actionUrl = (raw: unknown): { origin: string; pathname: string } | undefined => {
    if (typeof raw !== "string" || raw.length > 2_048 || hasControlCharacter(raw)) return undefined;
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return undefined;
    }
    if (url.protocol !== "https:" || !url.hostname) return undefined;
    let redactNext = false;
    const pathname = url.pathname
      .split("/")
      .map((segment) => {
        let decoded: string;
        try {
          decoded = decodeURIComponent(segment);
        } catch {
          return "[REDACTED]";
        }
        const secretLabel = CARD_SECRET_PATH_LABEL.test(decoded);
        const redact =
          redactNext ||
          /%[0-9a-f]{2}|[A-Za-z0-9_~+-]{32,}/i.test(decoded) ||
          /[/?#@]/.test(decoded) ||
          CARD_SECRET_PATH_ASSIGNMENT.test(decoded) ||
          sanitizeErrorMessage(redactSecrets(decoded)) !== decoded ||
          hasControlCharacter(decoded);
        redactNext = secretLabel;
        return redact ? "[REDACTED]" : segment;
      })
      .join("/");
    const bytes = Buffer.byteLength(url.origin + pathname, "utf8");
    if (bytes > MAX_CARD_FIELD_BYTES || bytes > budget.textBytes) {
      result.truncated = true;
      return undefined;
    }
    budget.textBytes -= bytes;
    return { origin: url.origin, pathname };
  };
  const captureTextLinks = (raw: unknown) => {
    if (typeof raw !== "string") return;
    // Only a bounded subset of Markdown links; no Markdown/HTML renderer or fetch.
    const bounded = raw.slice(0, 4_096);
    const pattern = /\[([^\]\r\n]{1,160})\]\(\s*(https:\/\/[^\s<>)]{1,2048})\s*\)/gi;
    for (const match of bounded.matchAll(pattern)) {
      if (budget.links <= 0) {
        result.truncated = true;
        break;
      }
      const url = actionUrl(match[2]);
      if (!url) continue;
      const label = takeText(match[1]) ?? null;
      result.links.push({ label, url });
      budget.links--;
    }
  };
  try {
    const root = object(content);
    // Inline string content is deliberately not parsed: the SDK supplies objects.
    if (!root || own(root, "type") !== "AdaptiveCard") {
      result.availability = "unavailable";
      return result;
    }
    const pending: Array<{ value: unknown; depth: number; inline?: boolean }> = [
      { value: root, depth: 0 },
    ];
    const seen = new Set<object>();
    const push = (value: unknown, depth: number, inline = false) => {
      if (value === undefined) return;
      if (depth > MAX_CARD_DEPTH || pending.length >= budget.nodes) {
        result.truncated = true;
        return;
      }
      pending.push({ value, depth, inline });
    };
    const children = (raw: unknown, depth: number, inline = false) => {
      if (raw === undefined) return;
      if (!Array.isArray(raw) || Object.getPrototypeOf(raw) !== Array.prototype)
        throw new Error("array");
      const count = Math.min(raw.length, Math.max(0, budget.nodes - pending.length));
      if (count < raw.length) result.truncated = true;
      for (let index = count - 1; index >= 0; index--) {
        const descriptor = Object.getOwnPropertyDescriptor(raw, String(index));
        if (!descriptor || !Object.hasOwn(descriptor, "value")) throw new Error("array accessor");
        push(descriptor.value, depth, inline);
      }
    };
    while (pending.length > 0) {
      if (budget.nodes <= 0) {
        result.truncated = true;
        break;
      }
      budget.nodes--;
      const item = pending.pop()!;
      if (item.inline && typeof item.value === "string") {
        if (result.textBlocks.length >= 20) result.truncated = true;
        else {
          const text = takeText(item.value);
          if (text) result.textBlocks.push(text);
          captureTextLinks(item.value);
        }
        continue;
      }
      const node = object(item.value);
      if (!node || seen.has(node)) throw new Error("node");
      seen.add(node);
      const type = own(node, "type");
      const depth = item.depth + 1;
      if (typeof type !== "string" || type.length > 64) throw new Error("type");
      if (CARD_ACTION_TYPES.has(type)) {
        if (result.actions.length >= 20) result.truncated = true;
        else {
          const title = takeText(own(node, "title"));
          const rawUrl = type === "Action.OpenUrl" ? own(node, "url") : undefined;
          const url = actionUrl(rawUrl);
          result.actions.push({
            type,
            ...(title ? { title } : {}),
            ...(url ? { url } : {}),
            ...(type === "Action.OpenUrl" && !url ? { urlOmitted: true } : {}),
          });
        }
        if (type === "Action.ShowCard") push(own(node, "card"), depth);
      } else if (type === "TextBlock" || type === "TextRun") {
        if (result.textBlocks.length >= 20) result.truncated = true;
        else {
          const rawText = own(node, "text");
          const text = takeText(rawText);
          if (text) result.textBlocks.push(text);
          captureTextLinks(rawText);
        }
        if (type === "TextRun") push(own(node, "selectAction"), depth);
      } else if (type === "AdaptiveCard") {
        children(own(node, "actions"), depth);
        children(own(node, "body"), depth);
        push(own(node, "selectAction"), depth);
      } else if (type === "Container" || type === "Column") {
        children(own(node, "items"), depth);
        push(own(node, "selectAction"), depth);
      } else if (type === "ColumnSet") {
        children(own(node, "columns"), depth);
        push(own(node, "selectAction"), depth);
      } else if (type === "ActionSet") children(own(node, "actions"), depth);
      else if (type === "RichTextBlock") children(own(node, "inlines"), depth, true);
      else if (type === "ImageSet") children(own(node, "images"), depth);
      else if (type === "Image") push(own(node, "selectAction"), depth);
      else result.unsupportedElementsOmitted = true;
    }
  } catch {
    // Preserve the executed turn; malformed optional diagnostics must not cause replay.
    result.availability = "unavailable";
    result.textBlocks = [];
    result.links = [];
    result.actions = [];
  }
  return result;
}

function summarizeResponseAttachments(
  activities: Record<string, unknown>[],
  includeCardDiagnostics: boolean,
  startActivityCount: number,
): {
  attachments: CopilotExecutionResponseAttachment[];
  truncated: boolean;
} {
  const summaries: CopilotExecutionResponseAttachment[] = [];
  const cardBudget = { nodes: MAX_CARD_NODES, textBytes: MAX_CARD_TEXT_BYTES, links: 5 };
  let truncated = false;
  for (const [activityIndex, activity] of activities.entries()) {
    const rawAttachments = activity["attachments"];
    if (rawAttachments === undefined) continue;
    const responseAttachments = rawAttachments as unknown[];
    const remainingSlots = MAX_RESPONSE_ATTACHMENTS - summaries.length;
    if (responseAttachments.length > remainingSlots) truncated = true;
    for (const raw of responseAttachments.slice(0, remainingSlots)) {
      if (!isPlainObject(raw) || typeof raw["contentType"] !== "string") {
        throw new ApiError(
          "Execute Copilot Studio agent: SDK returned a malformed response attachment",
          502,
        );
      }
      const contentType = raw["contentType"];
      if (!COPILOT_ATTACHMENT_CONTENT_TYPE_PATTERN.test(contentType) || contentType.length > 200) {
        throw new ApiError(
          "Execute Copilot Studio agent: SDK returned an invalid response attachment type",
          502,
        );
      }
      const name = raw["name"];
      summaries.push({
        contentType,
        ...(typeof name === "string" && name.length <= 255
          ? { name: safeAgentText(name, 255).value }
          : {}),
        hasInlineContent: raw["content"] !== undefined,
        hasContentUrl: typeof raw["contentUrl"] === "string",
        ...(includeCardDiagnostics &&
        contentType.toLowerCase() === "application/vnd.microsoft.card.adaptive"
          ? {
              cardDiagnostics: responseCardDiagnostics(
                raw["content"],
                activityIndex < startActivityCount ? "start" : "turn",
                cardBudget,
              ),
            }
          : {}),
      });
    }
  }
  return {
    attachments: summaries,
    truncated: truncated || summaries.some((item) => item.cardDiagnostics?.truncated === true),
  };
}

/**
 * Bounded protocol-field observations, not a business-result classifier.
 * Do not inspect text, trace values, tool bodies, channelData or identities.
 * The SDK yields all activity types but does not expose clean SSE end versus
 * transport close, nor promise delivery of every service execution signal.
 */
function summarizeActivitySignals(
  activities: Record<string, unknown>[],
  startActivityCount: number,
): CopilotExecutionActivitySignals {
  const result: CopilotExecutionActivitySignals = {
    availability: "unavailable",
    signals: [],
    truncated: false,
    limitations:
      "Only returned endOfConversation activity fields are reported, not an authenticated " +
      "execution trace or proof of tool effects. Absence of a signal does not prove success. " +
      "Even completedSuccessfully describes a reported conversation end, not verification of " +
      "the requested action. Unknown codes and all trace/event/tool payloads are omitted. " +
      "The SDK iterator returning does not establish clean service completion.",
  };
  for (const [index, activity] of activities.entries()) {
    if (activity["type"] !== "endOfConversation") continue;
    result.availability = "reported";
    if (result.signals.length >= MAX_RESPONSE_ACTIVITY_SIGNALS) {
      result.truncated = true;
      continue;
    }
    const code = activity["code"];
    const recognized =
      typeof code === "string" &&
      (RECOGNIZED_END_CODES as readonly string[]).includes(code);
    result.signals.push({
      stage: index < startActivityCount ? "start" : "turn",
      provenance: "sdk_activity",
      type: "endOfConversation",
      codeStatus:
        code === undefined
          ? "missing"
          : typeof code !== "string" || code.length === 0
            ? "invalid"
            : recognized
              ? "recognized"
              : "unrecognized",
      ...(recognized ? { code: code as CopilotConversationEndCode } : {}),
    });
  }
  return result;
}

function normalizedExecutionResult(
  conversationId: string,
  collected: CollectedActivities,
  maxResponseChars: number,
  includeCardDiagnostics: boolean,
  startActivityCount: number,
): CopilotExecutionResult {
  validateActivityConversationIds(collected.activities, conversationId);

  const messageCandidates: Array<{ value: string; truncated: boolean }> = [];
  let latestTyping: { value: string; truncated: boolean } | undefined;
  let responseTruncated = collected.responseTruncated;
  for (const activity of collected.activities) {
    const rawText = activity["text"];
    if (typeof rawText !== "string" || rawText.length === 0) continue;
    const safe = safeAgentText(rawText, maxResponseChars);
    if (activity["type"] === "message") {
      if (messageCandidates.length < MAX_RESPONSE_MESSAGES) {
        if (messageCandidates.at(-1)?.value !== safe.value) messageCandidates.push(safe);
      } else {
        responseTruncated = true;
      }
    } else if (activity["type"] === "typing") {
      latestTyping = safe;
    }
  }

  const candidates =
    messageCandidates.length > 0
      ? messageCandidates
      : latestTyping !== undefined
        ? [latestTyping]
        : [];
  let remaining = maxResponseChars;
  const responses: string[] = [];
  for (const candidate of candidates) {
    const taken = candidate.value.slice(0, remaining);
    if (taken.length > 0) responses.push(taken);
    remaining -= taken.length;
    responseTruncated ||= candidate.truncated || taken.length < candidate.value.length;
    if (remaining === 0) {
      responseTruncated ||= responses.length < candidates.length;
      break;
    }
  }

  const attachmentResult = summarizeResponseAttachments(
    collected.activities,
    includeCardDiagnostics,
    startActivityCount,
  );
  responseTruncated ||= attachmentResult.truncated;
  return {
    conversationId,
    responses,
    completed: true,
    completionMeaning: "sdk_iterator_returned",
    actionOutcome: "unverified",
    activitySignals: summarizeActivitySignals(collected.activities, startActivityCount),
    responseTruncated,
    responseAttachments: attachmentResult.attachments,
  };
}

/** Client for authenticated Direct-to-Engine agent execution. */
export class CopilotStudioExecutionApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;
  private readonly clientFactory: CopilotExecutionSdkClientFactory;

  constructor(config: CopilotStudioExecutionApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = validateEnvironmentId(config.defaultEnvironmentId);
    this.clientFactory =
      config.clientFactory ?? ((settings, token) => new CopilotStudioClient(settings, token));
  }

  async executeAndWait(options: ExecuteCopilotAgentOptions): Promise<CopilotExecutionResult> {
    const parsed = normalizeOptions(options, this.defaultEnvironmentId);

    return wrapApiCall(async () => {
      const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
      const settings = createConnectionSettings(parsed.environmentId, parsed.agentSchemaName);
      // Intentionally instantiate per call. CopilotStudioClient tracks a mutable
      // current conversation internally and must not be shared between callers.
      const client = this.clientFactory(settings, token);
      const deadline = Date.now() + parsed.timeoutMs;
      let conversationId = parsed.conversationId;
      let dispatchedPhase: "start" | "turn" | undefined;
      const activities: Record<string, unknown>[] = [];

      try {
        if (conversationId === undefined) {
          dispatchedPhase = "start";
          const rawStarted = await beforeDeadline(
            client.startConversationWithResponse({
              emitStartConversationEvent: true,
              ...(parsed.locale !== undefined ? { locale: parsed.locale } : {}),
            }),
            deadline,
            "Start Copilot Studio conversation",
          );
          // Preserve a valid ID even if the remainder of the SDK response is
          // malformed. The start event was already dispatched and may have
          // invoked a topic or tool before response parsing failed.
          conversationId = validatedStartConversationId(rawStarted);
          const started = readStartResponse(rawStarted);
          conversationId = started.conversationId;
          for (const activity of started.activities) activities.push(activityObject(activity));
        }

        const messageActivity = Activity.fromObject({
          type: "message",
          text: parsed.message,
          ...(parsed.locale !== undefined ? { locale: parsed.locale } : {}),
          ...(parsed.attachments !== undefined ? { attachments: parsed.attachments } : {}),
        });
        dispatchedPhase = "turn";
        const startActivityCount = activities.length;
        const collected = await collectStream(
          client.executeStreaming(messageActivity, conversationId),
          activities,
          deadline,
        );
        return normalizedExecutionResult(
          conversationId,
          collected,
          parsed.maxResponseChars,
          parsed.includeResponseCardDiagnostics,
          startActivityCount,
        );
      } catch (error) {
        try {
          throwSdkError(error, "Execute Copilot Studio agent");
        } catch (mappedError) {
          if (dispatchedPhase !== undefined && mappedError instanceof Error) {
            const recoverableId =
              conversationId === undefined ? "" : ` Conversation ID: ${conversationId}.`;
            const phase = dispatchedPhase === "start" ? "start event" : "message turn";
            mappedError.message +=
              `${recoverableId} The ${phase}'s completion is unknown and agent tools may ` +
              "already have run; do not blindly retry the same request. " +
              (conversationId === undefined
                ? "No conversation ID was safely recovered; inspect Copilot Studio run history before retrying."
                : "Inspect or continue this conversation instead.");
          }
          throw mappedError;
        }
      }
    }, "Execute Copilot Studio agent");
  }
}
